import type { CanvasBoxes } from '../../api/bindings/CanvasBoxes.ts';
import { Bullets, ListTextarea } from './listUtils.tsx';
import type { EditProps, ViewProps } from './types.ts';

const BOX_LABELS: Record<keyof CanvasBoxes, string> = {
  key_partners: 'Key Partners',
  key_activities: 'Key Activities',
  value_proposition: 'Value Proposition',
  customer_relationships: 'Customer Relationships',
  customer_segments: 'Customer Segments',
  key_resources: 'Key Resources',
  channels: 'Channels',
  cost_structure: 'Cost Structure',
  revenue_streams: 'Revenue Streams',
};

function CanvasBox({ title, items, className }: { title: string; items: string[]; className?: string }) {
  return (
    <div className={`break-inside-avoid rounded-lg border border-gray-200 p-3 ${className ?? ''}`}>
      <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-gray-500 uppercase">{title}</h4>
      <Bullets items={items} />
    </div>
  );
}

export function CanvasView({ content }: ViewProps<'canvas'>) {
  const b = content.boxes;
  // Classic BMC arrangement on md+: 5 columns; partners & segments span both rows,
  // costs/revenue split the bottom. Stacks on small screens.
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-10">
      <CanvasBox title={BOX_LABELS.key_partners} items={b.key_partners} className="md:col-span-2 md:row-span-2" />
      <CanvasBox title={BOX_LABELS.key_activities} items={b.key_activities} className="md:col-span-2" />
      <CanvasBox title={BOX_LABELS.value_proposition} items={b.value_proposition} className="md:col-span-2 md:row-span-2" />
      <CanvasBox title={BOX_LABELS.customer_relationships} items={b.customer_relationships} className="md:col-span-2" />
      <CanvasBox title={BOX_LABELS.customer_segments} items={b.customer_segments} className="md:col-span-2 md:row-span-2" />
      <CanvasBox title={BOX_LABELS.key_resources} items={b.key_resources} className="md:col-span-2" />
      <CanvasBox title={BOX_LABELS.channels} items={b.channels} className="md:col-span-2" />
      <CanvasBox title={BOX_LABELS.cost_structure} items={b.cost_structure} className="md:col-span-5" />
      <CanvasBox title={BOX_LABELS.revenue_streams} items={b.revenue_streams} className="md:col-span-5" />
    </div>
  );
}

export function CanvasEdit({ content, onChange }: EditProps<'canvas'>) {
  const setBox = (key: keyof CanvasBoxes, values: string[]) =>
    onChange({ ...content, boxes: { ...content.boxes, [key]: values } });
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {(Object.keys(BOX_LABELS) as (keyof CanvasBoxes)[]).map((key) => (
        <ListTextarea
          key={key}
          label={BOX_LABELS[key]}
          values={content.boxes[key]}
          onChange={(values) => setBox(key, values)}
          rows={3}
        />
      ))}
    </div>
  );
}
